// store/forms/delForm/actions.js

import { makeProjectActions } from "./projects/delFormProjectsActions";
import { makeEmployeeActions } from "./employees/delFormEmployeesActions";
import { makeUnitActions } from "./units/delFormUnitsActions";
import { makeFieldNotesActions } from "./fieldNotes/delFormFieldNotesActions";
import { makeJsaActions } from "../jsaForm/jsaFormActions";
import { makeWasteDisposalFormActions } from "../wasteDisposalForm/wasteDisposalFormActions";
import { makeClientSignaturesActions } from "./clientSignatures/delFormClientSignaturesActions";
import { makeSdsReferencesActions } from "./sdsReferences/delFormSdsReferencesActions";
import { makeMiscActions } from "./misc/delFormMiscActions";
import { makePhotosActions } from "../photosForm/photosFormActions";
import { makeAutosaveErrorsActions } from "./autosaveErrors/delFormAutosaveErrorsActions";

const bump = (set, get, tabKey, patch = null) => {
  const s = get();
  const prev = s[tabKey] || {};
  const nextRev = (prev.rev || 0) + 1;
  const next = patch
    ? { [tabKey]: { ...prev, rev: nextRev, ...patch } }
    : { [tabKey]: { ...prev, rev: nextRev } };
  set(next);
};

export const makeDelFormActionsForTab = (set, get, tabKey) => {
  const api = {
    // tiny helpers shared across sections
    getDraft: () => {
      const slice = get()[tabKey];
      if (!slice || !slice.currentDel) {
        throw new Error(
          `No currentDel found in ${tabKey}. Did you call startNewDel() or fetchDel()?`
        );
      }
      return slice.currentDel;
    },
    bump: (patch) => bump(set, get, tabKey, patch),

    // UI update helpers (namespaced under delForm)
    setProjectUi: (patch) => {
      const s = get();
      const prev = s[tabKey].delForm.projectUI;
      const nextPatch = typeof patch === "function" ? patch(prev) : patch;
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            projectUI: { ...prev, ...nextPatch },
          },
        },
      });
    },
    setEmployeeUi: (patch) => {
      const s = get();
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            employeeUI: { ...s[tabKey].delForm.employeeUI, ...patch },
          },
        },
      });
    },
    setUnitsUi: (patch) => {
      const s = get();
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            unitsUI: { ...s[tabKey].delForm.unitsUI, ...patch },
          },
        },
      });
    },
    setFieldNotesUi: (patch) => {
      const s = get();
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            fieldNotesUI: { ...s[tabKey].delForm.fieldNotesUI, ...patch },
          },
        },
      });
    },
    setClientSignaturesUi: (patch) => {
      const s = get();
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            clientSignaturesUI: {
              ...s[tabKey].delForm.clientSignaturesUI,
              ...patch,
            },
          },
        },
      });
    },
    setJsaUi: (patch) => {
      const s = get();
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            jsaFormUI: { ...s[tabKey].delForm.jsaFormUI, ...patch },
          },
        },
      });
    },
    setWasteDisposalUi: (patch) => {
      const s = get();
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            wasteDisposalFormUI: {
              ...s[tabKey].delForm.wasteDisposalFormUI,
              ...patch,
            },
          },
        },
      });
    },
    setMiscUi: (patch) => {
      const s = get();
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            miscUI: {
              ...s[tabKey].delForm.miscUI,
              ...patch,
            },
          },
        },
      });
    },
    setPhotosUi: (patch) => {
      const s = get();
      set({
        [tabKey]: {
          ...s[tabKey],
          delForm: {
            ...s[tabKey].delForm,
            photosUI: {
              ...s[tabKey].delForm.photosUI,
              ...patch,
            },
          },
        },
      });
    },
    getPhotosUi: () => {
      return get()[tabKey].delForm.photosUI;
    },
  };

  // --- Broadcast helpers ---

  const toCamelCase = (str) =>
    str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

  const applyPatchToObject = (target, patch, skipKeys = ["id", "action"]) => {
    Object.entries(patch).forEach(([key, value]) => {
      if (skipKeys.includes(key)) return;
      const camelKey = toCamelCase(key);
      const setterName = `set${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
      if (typeof target[setterName] === "function") {
        target[setterName](value);
      } else if (camelKey in target) {
        target[camelKey] = value;
      }
    });
  };

  // Build section action objects first so handleBroadcast can reference them
  const projects = makeProjectActions(api);
  const employees = makeEmployeeActions(api);
  const units = makeUnitActions(api);
  const fieldNotes = makeFieldNotesActions(api);
  const clientSignatures = makeClientSignaturesActions(api);
  const sdsReferences = makeSdsReferencesActions(api);
  const jsa = makeJsaActions(set, get, tabKey);
  const wasteDisposal = makeWasteDisposalFormActions(set, get, tabKey);

  const handleBroadcast = (patchData) => {
    const { patch_type, patch } = patchData;

    switch (patch_type) {
      case "project":
        if (projects.handleBroadcast) {
          projects.handleBroadcast(patch, applyPatchToObject);
        }
        // Handle available project-specific units attached to project change broadcasts
        if (patch.available_project_specific_units !== undefined) {
          units.setAvailableProjectUnits(patch.available_project_specific_units);
        }
        if (patch.project_type !== undefined) {
          const draft = api.getDraft();
          if (draft) {
            draft.projectInfo.projectType = patch.project_type || null;
          }
        }
        break;

      case "employee":
        if (employees.handleBroadcast) {
          employees.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "shift":
        if (employees.handleShiftBroadcast) {
          employees.handleShiftBroadcast(patch, applyPatchToObject);
        }
        break;

      case "unit":
        if (units.handleBroadcast) {
          units.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "misc_unit":
        if (units.handleMiscUnitBroadcast) {
          units.handleMiscUnitBroadcast(patch, applyPatchToObject);
        }
        break;

      case "rental_unit":
        if (units.handleRentalUnitBroadcast) {
          units.handleRentalUnitBroadcast(patch, applyPatchToObject);
        }
        break;

      case "project_specific_unit":
        if (units.handleProjectSpecificUnitBroadcast) {
          units.handleProjectSpecificUnitBroadcast(patch, applyPatchToObject);
        }
        break;

      case "field_notes":
        if (fieldNotes.handleBroadcast) {
          fieldNotes.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "field_notes_activity":
        if (fieldNotes.handleActivityBroadcast) {
          fieldNotes.handleActivityBroadcast(patch, applyPatchToObject);
        }
        break;

      case "field_notes_reorder":
        if (fieldNotes.handleReorderBroadcast) {
          fieldNotes.handleReorderBroadcast(patch);
        }
        break;

      case "client_signature":
        if (clientSignatures.handleBroadcast) {
          clientSignatures.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "sds_reference":
        sdsReferences.handleBroadcast(patch);
        break;

      default:
        console.warn(`Unknown DEL broadcast patch_type: ${patch_type}`);
    }
  };

  return Object.freeze({
    projects,
    employees,
    units,
    fieldNotes,
    clientSignatures,
    sdsReferences,
    jsa,
    wasteDisposal,
    misc: makeMiscActions(api),
    photos: makePhotosActions({
      getFormData: api.getDraft,
      setPhotosUi: api.setPhotosUi,
      getPhotosUi: api.getPhotosUi,
      bump: api.bump,
    }),
    autosaveErrors: makeAutosaveErrorsActions(set, get, tabKey),
    handleBroadcast,
    bump: api.bump,
  });
};
